import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Sun, Wind, Thermometer, MapPin } from "lucide-react";
import { api } from "../lib/api";

const PORT_IDS = [
  { id: "brehec", label: "Bréhec" },
  { id: "paimpol", label: "Paimpol" },
  { id: "saint-quay", label: "Saint-Quay" },
  { id: "erquy", label: "Erquy" },
];

function windCardinal(deg) {
  if (deg == null) return "";
  const dirs = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
  return dirs[Math.round(deg / 45) % 8];
}

export default function WeatherWidget({ defaultPort = "brehec" }) {
  const [port, setPort] = useState(defaultPort);
  const { data, isLoading } = useQuery({
    queryKey: ["weather", port],
    queryFn: async () => (await api.get(`/weather/${port}`)).data,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div data-testid="weather-widget" className="bg-blanc border border-brume rounded-2xl p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest text-bretagne font-bold inline-flex items-center gap-1.5">
          <Sun className="w-3.5 h-3.5" /> Météo marine
        </div>
        <div className="flex gap-1">
          {PORT_IDS.map((p) => (
            <button
              key={p.id}
              data-testid={`weather-port-${p.id}`}
              onClick={() => setPort(p.id)}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full transition ${
                port === p.id ? "bg-ocean text-blanc" : "bg-brume text-bretagne hover:bg-bretagne/15"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <h3 className="mt-3 font-display text-lg font-bold text-ocean inline-flex items-center gap-1.5">
        <MapPin className="w-4 h-4 text-corail" /> {data?.port?.name || "—"}
      </h3>

      {isLoading || !data ? (
        <div className="mt-4 text-sm text-bretagne">Chargement…</div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <Stat icon={Thermometer} label="Température" value={data.temp_c != null ? `${Math.round(data.temp_c)}°C` : "—"} />
          <Stat icon={Wind} label="Vent" value={data.wind_kmh != null ? `${Math.round(data.wind_kmh)} km/h ${windCardinal(data.wind_dir)}` : "—"} />
        </div>
      )}
      <p className="mt-3 text-[10px] text-bretagne/60 leading-relaxed">
        Données indicatives via Open-Meteo. Vérifiez toujours la météo officielle avant de prendre la mer.
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-sable rounded-xl px-3 py-2.5 border border-brume">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-bretagne/80 font-bold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-display text-base font-bold text-ocean leading-none mt-1">{value}</div>
      {sub && <div className="text-[10px] text-bretagne mt-0.5">{sub}</div>}
    </div>
  );
}
